/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import type { FC, ReactNode } from 'react';

import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import { useCallback, useState } from 'react';
import fallbackImage from 'src/assets/dot.svg'; // Adjust the path as needed
import { RouterLink } from 'src/components/router-link';
import { useSettings } from 'src/hooks/use-settings';

interface SideNavItemProps {
  active?: boolean;
  children?: ReactNode;
  depth?: number;
  disabled?: boolean;
  external?: boolean;
  icon?: string | null;
  label?: React.ReactNode;
  open?: boolean;
  route?: string | null;
  title: string;
  collapsed?: boolean;
}

export const SideNavItem: FC<SideNavItemProps> = props => {
  const {
    active,
    children,
    depth = 0,
    disabled,
    external,
    icon,
    label,
    open: openProp,
    route,
    title,
    collapsed = false,
  } = props;
  const [open, setOpen] = useState<boolean>(!!openProp);
  const { handleSidebarCollapse } = useSettings();

  const handleToggle = useCallback((): void => {
    if (collapsed && children) {
      handleSidebarCollapse();
    }
    setOpen(prevOpen => !prevOpen);
  }, [collapsed, children, handleSidebarCollapse]);

  // Icons can be defined at top level only, deep levels have bullets instead of actual icons.
  let StartIcon: ReactNode;
  if (depth === 0) {
    // StartIcon = <MenuIcon />;
    StartIcon = icon ? (
      <img
        alt={`icon-${typeof label === 'string' ? label : 'default'}`}
        src={icon}
        style={{ height: 24, width: 24, filter: 'brightness(0) invert(1)' }}
        onError={e => {
          const target = e.target as HTMLImageElement;
          target.onerror = null; // Prevents infinite loop in case fallbackSrc also fails
          target.src = fallbackImage;
        }}
      />
    ) : (
      <img
        alt={`icon-${typeof label === 'string' ? label : 'default'}`}
        src={fallbackImage}
        style={{ height: 24, width: 24, filter: 'brightness(0) invert(1)' }}
      />
    );
  } else {
    StartIcon = (
      <Box
        sx={{
          alignItems: 'center',
          display: 'center',
          height: 20,
          justifyContent: 'center',
          width: 20,
        }}
      >
        {icon ? (
          <img
            alt={`icon-${typeof label === 'string' ? label : 'default'}`}
            src={icon}
            style={{ height: 24, width: 24, filter: 'brightness(0) invert(1)' }}
            onError={e => {
              const target = e.target as HTMLImageElement;
              target.onerror = null; // Prevents infinite loop in case fallbackSrc also fails
              target.src = fallbackImage;
            }}
          />
        ) : (
          <img
            alt={`icon-${typeof label === 'string' ? label : 'default'}`}
            src={fallbackImage}
            style={{ height: 24, width: 24, filter: 'brightness(0) invert(1)' }}
          />
          // <Iconify icon="clarity:sync-line" />
        )}
      </Box>
    );
  }

  const offset = depth === 0 ? 0 : (depth - 1) * 16;

  const buttonContent = (
    <ButtonBase
      disabled={disabled}
      onClick={children ? handleToggle : undefined}
      sx={{
        'alignItems': 'center',
        'borderRadius': 1,
        'display': 'flex',
        'justifyContent': collapsed ? 'center' : 'flex-start',
        'pl': collapsed ? '8px' : `${16 + offset}px`,
        'pr': collapsed ? '8px' : '16px',
        'py': '6px',
        'textAlign': 'left',
        'width': '100%',
        ...(active && {
          ...(depth === 0 && {
            backgroundColor: 'var(--nav-item-active-bg)',
          }),
        }),
        '&:hover': {
          backgroundColor: 'var(--nav-item-hover-bg)',
        },
      }}
      {...(route && !children
        ? {
            component: external ? 'a' : RouterLink,
            href: route,
            ...(external && { target: '_blank' }),
          }
        : {})}
    >
      <Box
        component="span"
        sx={{
          alignItems: 'center',
          color: 'var(--nav-item-icon-color)',
          display: 'inline-flex',
          justifyContent: 'center',
          mr: collapsed ? 0 : 2,
          ...(active && {
            color: 'var(--nav-item-icon-active-color)',
          }),
        }}
      >
        {StartIcon}
      </Box>
      {!collapsed && (
        <>
          <Box
            component="span"
            sx={{
              color: 'var(--nav-item-color)',
              flexGrow: 1,
              fontFamily: theme => theme.typography.fontFamily,
              fontSize: depth > 0 ? 11 : 12,
              fontWeight: depth > 0 ? 500 : 600,
              lineHeight: '24px',
              whiteSpace: 'nowrap',
              ...(active && {
                color: 'var(--nav-item-active-color)',
              }),
              ...(disabled && {
                color: 'var(--nav-item-disabled-color)',
              }),
            }}
          >
            {title}
          </Box>
          {children &&
            (open ? (
              <Icon icon="material-symbols:arrow-downward-rounded" />
            ) : (
              <Icon icon="material-symbols:arrow-upward-rounded" />
            ))}
        </>
      )}
    </ButtonBase>
  );

  // Branch
  if (children) {
    return (
      <li>
        {collapsed ? (
          <Tooltip title={title} placement="right">
            {buttonContent}
          </Tooltip>
        ) : (
          buttonContent
        )}
        {!collapsed && (
          <Collapse in={open} sx={{ ml: 2, mt: 0.5 }}>
            {children}
          </Collapse>
        )}
      </li>
    );
  }

  // Leaf
  return (
    <li>
      {collapsed ? (
        <Tooltip title={title} placement="right">
          {buttonContent}
        </Tooltip>
      ) : (
        buttonContent
      )}
    </li>
  );
};
